import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import UserAvatars from '../../../components/UserAvatars';
import HtmlContent from '../../../components/HtmlContent';

const ViewDonorModal = ({ donorId, isOpen, onClose, onUpdateDonor }) => {
  const { currentLang, token, API_URL, getCsrfToken } = useAuth();
  const [donor, setDonor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Загружаем данные донора при открытии модального окна
  useEffect(() => {
    if (isOpen && donorId) {
      fetchDonor();
    }
  }, [isOpen, donorId]);

  // Функция для обновления данных донора
  const refreshDonorData = async () => {
    if (donorId) {
      await fetchDonor();
      // Уведомляем родительский компонент об обновлении
      if (onUpdateDonor && donor) {
        onUpdateDonor(donorId, donor);
      }
    }
  };

  const fetchDonor = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetch(`${API_URL}${currentLang}/seo-donors/${donorId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setDonor(data.data);
      } else {
        throw new Error(data.message || 'Ошибка загрузки донора');
      }
    } catch (error) {
      console.error('Ошибка загрузки донора:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Форматирование даты
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('ru-RU');
    } catch (error) {
      return dateString;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold">
              {currentLang === 'ru' ? 'Информация о доноре' : 'Donor Information'}
            </h3>
            <div className="flex space-x-2">
              <button
                onClick={refreshDonorData}
                disabled={loading}
                className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                title={currentLang === 'ru' ? 'Обновить данные' : 'Refresh data'}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
            </div>
          ) : donor ? (
            <div className="space-y-6">
              {/* Основная информация */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-md font-semibold mb-3">
                  {currentLang === 'ru' ? 'Основная информация' : 'Basic Information'}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {currentLang === 'ru' ? 'ID' : 'ID'}
                    </label>
                    <p className="text-sm text-gray-900">{donor.id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {currentLang === 'ru' ? 'Статус' : 'Status'}
                    </label>
                    <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                      donor.status === 1 ? 'bg-green-100 text-green-800' :
                      donor.status === 0 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {donor.status === 1 ? (currentLang === 'ru' ? 'Активный' : 'Active') :
                       donor.status === 0 ? (currentLang === 'ru' ? 'Неактивный' : 'Inactive') :
                       '-'}
                    </span>
                  </div>
                                     <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       {currentLang === 'ru' ? 'Дата создания' : 'Created Date'}
                     </label>
                     <p className="text-sm text-gray-900">{formatDate(donor.created_at)}</p>
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       {currentLang === 'ru' ? 'Закреплено' : 'Pinned'}
                     </label>
                     {donor.weight > 0 ? (
                       <div className="flex items-center space-x-1">
                         <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
                           <path d="M16 9V4h1V2H7v2h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1l1-1v-7H19v-2c-1.66 0-3-1.34-3-3z"/>
                         </svg>
                         <span className="text-sm text-yellow-600 font-medium">
                           {currentLang === 'ru' ? 'Закреплено' : 'Pinned'}
                         </span>
                       </div>
                     ) : (
                       <span className="text-sm text-gray-500">
                         {currentLang === 'ru' ? 'Не закреплено' : 'Not pinned'}
                       </span>
                     )}
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       {currentLang === 'ru' ? 'Доступ' : 'Access'}
                     </label>
                     {donor.access_users && donor.access_users.length > 0 ? (
                       <div className="space-y-2">
                         <UserAvatars 
                           users={donor.access_users} 
                           maxVisible={5} 
                           size="md" 
                         />
                         <div className="text-xs text-gray-500">
                           {donor.access_users.length} {currentLang === 'ru' ? 'пользователей' : 'users'} {currentLang === 'ru' ? 'с доступом' : 'with access'}
                         </div>
                       </div>
                     ) : (
                       <p className="text-sm text-gray-500">-</p>
                     )}
                   </div>
                </div>
              </div>

              {/* URL и название */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-md font-semibold mb-3">
                  {currentLang === 'ru' ? 'Детали донора' : 'Donor Details'}
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {currentLang === 'ru' ? 'URL донора' : 'Donor URL'}
                    </label>
                    <div className="flex items-center space-x-2">
                      <a 
                        href={donor.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm break-all"
                      >
                        {donor.url}
                      </a>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(donor.url);
                          // Показываем уведомление
                          const notification = document.createElement('div');
                          notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
                          notification.textContent = currentLang === 'ru' ? 'URL скопирован!' : 'URL copied!';
                          document.body.appendChild(notification);
                          setTimeout(() => {
                            if (document.body.contains(notification)) {
                              document.body.removeChild(notification);
                            }
                          }, 3000);
                        }}
                        className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                        title={currentLang === 'ru' ? 'Копировать URL' : 'Copy URL'}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                                     <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       {currentLang === 'ru' ? 'Название' : 'Title'}
                     </label>
                     <p className="text-sm text-gray-900">{donor.title || '-'}</p>
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       {currentLang === 'ru' ? 'Теги' : 'Tags'}
                     </label>
                     <p className="text-sm text-gray-900">{donor.tags || '-'}</p>
                   </div>
                  {donor.description && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {currentLang === 'ru' ? 'Описание' : 'Description'}
                      </label>
                      <div className="text-sm text-gray-900">
                        <HtmlContent content={donor.description} />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Статистика */}
              {donor.links_count !== undefined && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-md font-semibold mb-3">
                    {currentLang === 'ru' ? 'Статистика' : 'Statistics'}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {currentLang === 'ru' ? 'Количество ссылок' : 'Links Count'}
                      </label>
                      <p className="text-sm text-gray-900">{donor.links_count || 0}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              {currentLang === 'ru' ? 'Донор не найден' : 'Donor not found'}
            </div>
          )}

          <div className="flex justify-end pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              {currentLang === 'ru' ? 'Закрыть' : 'Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewDonorModal; 